"use client";

import type { TimelineContent } from "@/components/landing/registry/content";
import { pickBi } from "@/components/landing/registry/locale";
import {
	Eyebrow,
	MotionReveal,
	SectionHeader,
	useSectionMotion,
} from "@/components/landing/shared";

interface TimelineInnerProps {
	content: TimelineContent;
	locale: string;
}

/**
 * Static vertical step tracker — numbered nodes connected by a brand-colored
 * line, mirroring `Delivery/DeliveryTimeline`'s node/line styling but without
 * the auto-cycling courier animation (this is a generic process overview,
 * not a live tracking simulation).
 */
function TimelineInner(prop: TimelineInnerProps) {
	const { content, locale } = prop;
	const motion = useSectionMotion();
	const eyebrow = pickBi(content.eyebrow, locale);
	const title = pickBi(content.title, locale);
	const subtitle = pickBi(content.subtitle, locale);

	return (
		<>
			<MotionReveal motion={motion}>
				<SectionHeader
					eyebrow={eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : undefined}
					title={title}
					subtitle={subtitle}
					wrapperClassName="mx-auto max-w-2xl px-6 text-center"
				/>
			</MotionReveal>

			<div className="relative mx-auto mt-14 max-w-2xl px-6">
				<div
					className="absolute w-0.5 bg-border"
					style={{ insetInlineStart: "calc(1.25rem - 1px)", top: "1.25rem", bottom: "1.25rem" }}
				/>
				<div className="flex flex-col gap-10">
					{content.steps.map((step, i) => (
						<MotionReveal key={`${i}-${step.title.en}`} motion={motion} delayOffset={i * 140}>
							<div className="relative flex gap-6">
								<span
									className="relative z-10 grid h-10 w-10 shrink-0 place-items-center rounded-full text-sm font-bold"
									style={{ background: "var(--brand-1)", color: "#fff" }}
								>
									{i + 1}
								</span>
								<div className="pt-2">
									<h3 className="text-lg font-semibold">{pickBi(step.title, locale)}</h3>
									<p className="mt-1.5 leading-relaxed text-muted-foreground">
										{pickBi(step.body, locale)}
									</p>
								</div>
							</div>
						</MotionReveal>
					))}
				</div>
			</div>
		</>
	);
}

export default TimelineInner;
